import type { ButtonHTMLAttributes, ReactNode } from 'react'

import { useBeat, useWork } from '~/components/use-work'
import { cn } from '~/lib/cn'

/**
 * A press that has to wait says so on the thing pressed — AGENTS.md, and `/specs/app.spec.md`,
 * A press that waits. This is the one place that is built: every control whose press starts
 * work the page waits on — the server, a download, a share, the AI, a photo being shrunk —
 * wears it, rather than a "Saving…" of its own or a line of status under it.
 *
 * The control keeps its size and its look. What it shows — its label, its glyph — stays laid
 * out exactly where it was and is only hidden, so nothing about the control's box can change,
 * and a spinner stands over it in the control's own ink. The control is deaf to a second press
 * from the first moment; the spinner comes in only once the work has run past a beat, so work
 * that is done at once never flashes one.
 */

/** A mark turning, in the ink of whatever it stands in. */
export function Spinner({ className }: { className?: string | undefined }) {
  return (
    <svg
      aria-hidden
      viewBox="0 0 24 24"
      fill="none"
      className={cn('size-5 animate-spin', className)}
      data-spinner=""
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="2.5" />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  )
}

/**
 * What a working control shows in place of its content. The content stays where it is and is
 * only made clear — `display: contents` lets it go on taking part in the control's own layout,
 * its words go transparent and its glyphs go to nothing — and the spinner stands over it. The
 * control it is put in has to be `relative`, which is what the spinner is placed against.
 *
 * Made clear rather than hidden, because hidden content is no part of a control's name, and a
 * working "Save" is still called Save. Which is also why what goes in here is a control's
 * content — its words and its glyphs — and never a control itself.
 */
export function Working({
  shown,
  spinner,
  children,
}: {
  shown: boolean
  /** The spinner's own classes: its size, where the glyph it stands for is not a label's. */
  spinner?: string | undefined
  children: ReactNode
}) {
  return (
    <>
      <span
        data-working={shown ? '' : undefined}
        className={cn('contents', shown && 'text-transparent [&>*]:opacity-0')}
      >
        {children}
      </span>
      {shown ? (
        <span className="pointer-events-none absolute inset-0 grid place-items-center">
          <Spinner className={spinner} />
        </span>
      ) : null}
    </>
  )
}

/**
 * A button whose press is work to wait on. Everything about it is an ordinary button's, with
 * the press run through [useWork] and its content through [Working]: pressed again while it
 * works, it does nothing, and it does not dim — it is not disabled, only busy.
 */
export function WorkingButton({
  onPress,
  working: outside = false,
  spinner,
  className,
  children,
  ...rest
}: Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> & {
  /** The work. It says its own failures — a toast, a line on the page — as the screen does. */
  onPress: () => Promise<unknown>
  /**
   * Work under way that this button did not start but is part of — the same save pressed from
   * the keyboard, say. It holds the button as its own work would.
   */
  working?: boolean
  spinner?: string | undefined
}) {
  const work = useWork()
  const busy = work.working || outside
  const shown = useBeat(busy)
  return (
    <button
      type="button"
      {...rest}
      aria-busy={busy || undefined}
      className={cn('relative', className)}
      onClick={() => {
        if (busy) {
          return
        }
        void work.run(onPress).catch(() => undefined)
      }}
    >
      <Working shown={shown} spinner={spinner}>
        {children}
      </Working>
    </button>
  )
}
